import React, { useRef, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import MovieCard from "./MovieCard";
import { GrFormNext1 } from "../Icon/Icon";
import { Link } from "react-router-dom";
import { FaChevronRight } from "react-icons/fa";
import { gsap } from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
gsap.registerPlugin(ScrollToPlugin);

const SkeletonCard = () => (
  <div className="relative flex-shrink-0 group cursor-pointer w-1/2 sm:w-1/3 lg:basis-1/3 xl:basis-1/4">
    <div className="aspect-[16/9] w-full rounded-lg overflow-hidden shadow-md animate-pulse bg-neutral-700" />
  </div>
);

const CategoryCarousel = ({
  title,
  movies,
  codeCountry,
  nameCountry,
  gradient,
}) => {
  const scrollRef = useRef();
  const cardRef = useRef();
  const [scrollAmount, setScrollAmount] = useState(300);

  useEffect(() => {
    if (cardRef.current) {
      const cardWidth = cardRef.current.offsetWidth;
      const style = getComputedStyle(scrollRef.current);
      const gap = parseInt(style.columnGap) || parseInt(style.gap) || 12;
      setScrollAmount(cardWidth + gap);
    }
  }, [movies]);

  const scrollLeft = () => {
    if (!scrollRef.current) return;
    gsap.to(scrollRef.current, {
      scrollTo: { x: scrollRef.current.scrollLeft - scrollAmount },
      duration: 0.1,
      ease: "power2.out",
    });
  };

  const scrollRight = () => {
    if (!scrollRef.current) return;
    gsap.to(scrollRef.current, {
      scrollTo: { x: scrollRef.current.scrollLeft + scrollAmount },
      duration: 0.1,
      ease: "power2.out",
    });
  };

  const sectionId = `carousel-${codeCountry}`;

  return (
    <section
      className="relative rounded-lg text-white px-0 py-2 lg:px-6 lg:py-6"
      role="region"
      aria-labelledby={sectionId}
    >
      <div className="flex flex-col lg:flex-row gap-1">
        <div className="flex flex-row sm:flex-col justify-between sm:justify-start sm:items-start w-full lg:w-[30%] gap-2 sm:gap-0">
          <div>
            <h2
              id={sectionId}
              className="lg:w-[100%] text-2xl xl:text-3xl font-bold font-seogoe bg-clip-text inline-block"
              style={{
                background: gradient,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              {title}
            </h2>
            <div
              className="hidden sm:block h-1 w-20 lg:w-40 rounded-full mt-1 lg:mt-3"
              style={{
                background: gradient,
              }}
            />

            <p className="text-slate-400 text-sm mt-1 lg:mt-3 font-medium">
              Khám phá bộ sưu tập phim {nameCountry}
            </p>
          </div>

          <Link
            to={`/quoc-gia/${codeCountry}`}
            className="group hidden md:flex items-center gap-2 text-sm font-semibold text-white/80 transition-transform duration-150 hover:translate-y-[1px] mt-0 lg:mt-2"
            aria-label={`Xem tất cả phim thuộc quốc gia ${nameCountry}`}
          >
            <span className="no-underline group-hover:underline group-hover:underline-offset-2">
              Xem tất cả
            </span>
            <GrFormNext1 className="items-centerh-7 w-7 text-white/80 group-hover:text-white" />
          </Link>

          <Link
            to={{ pathname: `/quoc-gia/${codeCountry}` }}
            className="sm:hidden flex items-center justify-center mb-1"
            aria-label={`Xem tất cả phim thuộc quốc gia ${nameCountry}`}
          >
            <FaChevronRight className="text-[rgb(185,147,232)] w-4 h-4" />
          </Link>
        </div>

        <div className="relative w-full">
          <button
            onClick={scrollLeft}
            aria-label="Cuộn trái danh sách phim"
            className="hidden lg:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full transition"
          >
            <ChevronLeft className="text-white w-6 h-6" />
          </button>
          <div
            ref={scrollRef}
            className="flex gap-3 sm:gap-4 pb-2 overflow-x-auto scroll-smooth"
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
            role="region"
            aria-label={`Danh sách phim theo quốc gia ${nameCountry}`}
          >
            {movies.length === 0
              ? Array.from({ length: 5 }).map((_, idx) => (
                  <SkeletonCard key={idx} />
                ))
              : movies.map((movie, idx) => (
                  <MovieCard
                    key={movie.id || idx}
                    movie={movie}
                    ref={idx === 0 ? cardRef : null}
                  />
                ))}
          </div>
          <button
            onClick={scrollRight}
            aria-label="Cuộn phải danh sách phim"
            className="hidden lg:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full transition"
          >
            <ChevronRight className="text-white w-6 h-6" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default CategoryCarousel;
